type PrintRequestParams = {
  service: string;
  id: string;
};

type RequestTemplateDocument = (params: PrintRequestParams) => Promise<Blob>;

type NotifyFn = (toast: { title: string; description: string; type: string }) => void;

type TranslateFn = (key: string) => string;

interface PrintRequestedTemplateArgs {
  id?: string;
  service: string;
  requestTemplateDocument: RequestTemplateDocument;
  notify: NotifyFn;
  t: TranslateFn;
}

export const printRequestedTemplate = async ({
  id,
  service,
  requestTemplateDocument,
  notify,
  t,
}: PrintRequestedTemplateArgs) => {
  if (!id) return;

  const userAgent = window.navigator.userAgent;
  const isSafariBrowser =
    /Safari/i.test(userAgent) &&
    !/Chrome|CriOS|Chromium|Edg|OPR|FxiOS|Firefox|Android/i.test(userAgent);
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    notify({
      title: t("common.error"),
      description: t("common.unable-to-open-print-window"),
      type: "error",
    });
    return;
  }

  let blobUrl: string | null = null;
  let hasTriggeredPrint = false;
  let isPrintSessionFinished = false;
  let printTriggerTimeoutId: number | null = null;
  let printWindowLoadFallbackTimeoutId: number | null = null;

  const cleanupBlobUrl = () => {
    if (!blobUrl) return;
    URL.revokeObjectURL(blobUrl);
    blobUrl = null;
  };

  const clearTimeoutIfSet = (timeoutId: number | null) => {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  };

  const finishPrintSession = ({ closeWindow = false }: { closeWindow?: boolean } = {}) => {
    if (isPrintSessionFinished) return;
    isPrintSessionFinished = true;

    clearTimeoutIfSet(printTriggerTimeoutId);
    clearTimeoutIfSet(printWindowLoadFallbackTimeoutId);

    printWindow.onafterprint = null;
    printWindow.onbeforeunload = null;

    cleanupBlobUrl();
    if (closeWindow && !printWindow.closed) {
      printWindow.close();
    }
  };

  try {
    const rawBlob = await requestTemplateDocument({ id, service });
    const pdfBlob =
      rawBlob.type === "application/pdf"
        ? rawBlob
        : new Blob([rawBlob], { type: "application/pdf" });
    blobUrl = URL.createObjectURL(pdfBlob);
    const onLoadPrintDelayMs = 300;
    const fallbackPrintDelayMs = 1000;

    const triggerPrint = () => {
      if (hasTriggeredPrint) return;
      hasTriggeredPrint = true;

      try {
        // Safari can emit beforeunload while navigating from about:blank to the blob URL.
        // Only clean up on unload after the print flow actually starts.
        if (isSafariBrowser) {
          printWindow.onbeforeunload = () => {
            finishPrintSession();
          };
        }
        printWindow.focus();
        printWindow.print();
      } catch {
        notify({
          title: t("common.error"),
          description: t("common.unable-to-open-print-window"),
          type: "error",
        });
        finishPrintSession({ closeWindow: true });
      }
    };

    printWindow.location.href = blobUrl;

    printWindow.onload = () => {
      printTriggerTimeoutId = window.setTimeout(() => {
        triggerPrint();
      }, onLoadPrintDelayMs);
    };

    // Fallback in case PDF viewer does not emit onload reliably.
    printWindowLoadFallbackTimeoutId = window.setTimeout(triggerPrint, fallbackPrintDelayMs);

    printWindow.onafterprint = () => {
      finishPrintSession({ closeWindow: true });
    };

    if (!isSafariBrowser) {
      printWindow.onbeforeunload = () => {
        finishPrintSession();
      };
    }
  } catch (err: unknown) {
    finishPrintSession({ closeWindow: true });
    notify({
      title: t("common.error"),
      description: err instanceof Error ? err.message : t("common.something-went-wrong"),
      type: "error",
    });
  }
};
